import { useTranslation } from 'react-i18next';
import styles from './Banner.module.scss';
import { useFlag } from '@livingroot/yandex-metrica-ab-react';
import { CLIENT_ID, SubmitButton } from '..';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useAppSelector } from '../../../app';
import { BannerData, useGetTagsQueryState } from '../../api';
import classNames from 'classnames';

export default function Banner(): JSX.Element {
  const { t } = useTranslation();
  const {
    value: [flagVal],
    ready,
  } = useFlag('banner', { clientId: CLIENT_ID });
  const { data: tagsData, isLoading: isBannerDataLoading } =
    useGetTagsQueryState();
  const cityKey = useAppSelector(state => state.app.cityKey);
  const [bannerData, setBannerData] = useState<BannerData | null>(null);
  const [imageSize, setImageSize] = useState<string>('');
  const isExperiment = cityKey === 'stpb';

  // В случае если загрузка скрипта exp.js блокируется,
  // ready никогда не будет установлено true
  // тогда по истечение 5 секунд баннер должен быть показан в любом случае
  const [fallback, setFallback] = useState<boolean>(false);
  useEffect(() => {
    if (isExperiment) {
      const timer = setTimeout(() => {
        setFallback(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  useLayoutEffect(() => {
    if (tagsData?.banner) {
      setBannerData(tagsData.banner);
    }
  }, [tagsData]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(([e]) => {
      const bodyWidth = e.borderBoxSize[0].inlineSize;

      let newImageSize = '';

      if (bodyWidth < 576) {
        newImageSize = '-575w';
      } else if (bodyWidth < 768) {
        newImageSize = '-767w';
      } else if (bodyWidth < 992) {
        newImageSize = '-991w';
      }

      if (window.devicePixelRatio > 1) {
        newImageSize = newImageSize + '-2x';
      }

      setImageSize(newImageSize);
    });

    resizeObserver.observe(document.body, { box: 'border-box' });

    return () => resizeObserver.disconnect();
  }, []);

  const imgSrc =
    bannerData?.image || `/assets/img/hero/${cityKey}${imageSize}.jpg`;

  // Баннер должен быть показан только в случае
  // если A/B-тест не запущен на сайте,
  // или если это сайт Busguide,
  // или если данные A/B-теста загружены (ready = true) и при этом флаг = 0 или данные баннера загружены.
  // В противном случае показывается баннеро по умолчанию.
  const imgCondition =
    !isExperiment ||
    (ready &&
      (+flagVal === 0 ||
        bannerData ||
        (flagVal === undefined && !isBannerDataLoading))) ||
    (fallback && !ready);

  return (
    <section className={styles.root}>
      {((ready && +flagVal !== 0) || flagVal === undefined) && bannerData && (
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <p className={styles.text}>{bannerData.text}</p>
            <SubmitButton
              className={classNames(styles.button, 'BannerButton')}
              text={bannerData.button.text}
              href={bannerData.button.href}
              isButton={false}
            />
          </div>
        </div>
      )}
      <div className={styles.background}>
        {imgCondition && (
          <img
            src={imgSrc}
            alt={t('banner_alt_neva')}
          />
        )}
      </div>
    </section>
  );
}
